import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';

interface MySpacesEmptyStateProps {
  hasSpaces: boolean;
}

/**
 * Empty state for the My Spaces tab.
 *
 * Two variants:
 * 1. Authenticated + has spaces but empty feed -> prompt to post
 * 2. Guest or no spaces joined -> prompt to explore spaces (with guest-specific copy)
 *
 * Best practices applied:
 * - Wrapped with memo() to prevent unnecessary re-renders
 * - Named function for better React DevTools debugging
 */
export const MySpacesEmptyState = memo(function MySpacesEmptyState({
  hasSpaces,
}: MySpacesEmptyStateProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Has spaces but empty feed
  if (isAuthenticated && hasSpaces) {
    return (
      <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
          <img src="/home-bottom-nav-icon.svg" alt="" className="w-10 h-10" aria-hidden="true" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Your spaces are quiet for now</h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-3">
          Be the first to post something here.
        </p>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          In the meantime, check out the Meetverse — the public feed where you can see what people
          around you are looking for and offer help.
        </p>
        <button
          type="button"
          onClick={() => {
            navigate('/create-question');
          }}
          className="bg-mb-teal hover:bg-mb-teal-dark text-mb-ink px-7 py-3 rounded-full text-sm font-semibold"
        >
          Create a post
        </button>
      </div>
    );
  }

  // Guest or no spaces -- both show Explore Spaces
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
      <img
        src="/assets/spaces_empty_illustration.svg"
        alt=""
        className="w-40 mb-6"
        aria-hidden="true"
      />
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Find your people</h2>
      <p className="text-sm text-gray-500 leading-relaxed mb-3">
        Spaces are where people come together around a shared context — an event, a community, or a
        common interest. Join a space to see what people there are looking for and how you can help.
      </p>
      <p className="text-sm text-gray-500 leading-relaxed mb-6">
        {isAuthenticated
          ? "You can also check the Meetverse — the public feed where you'll see what people around you need, no space required."
          : "You can also check the Meetverse — the public feed where you'll see what people around you need."}
      </p>
      <button
        type="button"
        onClick={() => {
          navigate('/events');
        }}
        className="bg-mb-teal hover:bg-mb-teal-dark text-mb-ink px-7 py-3 rounded-full text-sm font-semibold"
      >
        Explore Spaces
      </button>
    </div>
  );
});
